import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import placeHolderImage from "assets/images/no-image-placeholder.jpg";
import ArgonButton from "components/ArgonButton";
import "./ReviewPanelModal.css";
import { getActivityData } from "services/activityService";
import { useEffect, useState } from "react";
import AvatarWithLoader from "../../../Components/Avatar/AvatarWithLoader";

function ActivityReviewPanelModal({ open, handleClose, activityId }) {
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (activityId && open) {
      setLoading(true);
      setError(null);
      getActivityData(activityId)
        .then((response) => {
          if (response.success) {
            setActivityData(response.data);
          } else {
            setError("Failed to fetch activity data");
          }
        })
        .catch((err) => {
          setError("Error loading activity data");
          console.error("Error fetching activity data:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [activityId, open]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Dialog>
    );
  }

  if (!activityData) {
    return null;
  }

  const {
    activity,
    creator,
    activityAddress,
    activityParticipants,
    activityParticipantsCount,
    category,
  } = activityData;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box display="flex" justifyContent="center" alignItems="center" pt={3}>
        <Typography fontSize={20} fontWeight={600} color={"#344767"}>
          Activity Review Panel
        </Typography>
      </Box>
      <DialogContent sx={{ p: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{ flexWrap: "wrap", gap: 2 }}
        >
          <MetaItem
            label="Created By"
            name={creator?.name || "Unknown"}
            img={creator?.avatar}
          />
        </Box>

        <Grid
          container
          borderColor={"#E0E0E0"}
          display="flex"
          justifyContent="center"
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            boxShadow: 2,
            p: 2,
          }}
        >
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <img
                alt={activity?.title || "Activity"}
                src={activity?.image || placeHolderImage}
                style={{
                  width: 350,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 10,
                  marginTop: 20,
                }}
              />
              <Box>
                <Typography
                  fontSize={20}
                  fontWeight={600}
                  color={"#172B4D"}
                  mt={1}
                >
                  {activity?.title || "Untitled Activity"}
                </Typography>
                {category && (
                  <Chip
                    label={category.name}
                    size="small"
                    sx={{
                      backgroundColor: "#fff7e6",
                      color: "#d46b08",
                      fontWeight: 600,
                    }}
                  />
                )}
                <Typography
                  fontSize={16}
                  fontWeight={400}
                  color={"#4F4F4F"}
                  mt={1}
                  mb={1}
                >
                  {activity?.description || "No description available"}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EventIcon fontSize="small" />
                    <Typography fontSize={14} fontWeight={400}>
                      {formatDate(activity?.datetime)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PeopleIcon fontSize="small" />
                    <Typography fontSize={14} fontWeight={400}>
                      {activityParticipantsCount || 0}/
                      {activity?.max_participants || 0}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOnIcon fontSize="small" />
                  <Typography fontSize={14} fontWeight={400}>
                    {activityAddress
                      ? activityAddress
                      : "Location not specified"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box p={2}>
              <Typography
                fontSize={16}
                fontWeight={600}
                pb={1}
                color={"#000000"}
              >
                Participants ({activityParticipantsCount || 0})
              </Typography>
              {activityParticipants && activityParticipants.length > 0 ? (
                activityParticipants.map((user, idx) => (
                  <Box
                    key={user._id || idx}
                    display="flex"
                    alignItems="center"
                    mb={1}
                    p={1}
                    boxShadow={"0 0 5px rgba(0, 0, 0, 0.2)"}
                    sx={{
                      borderRadius: 4,
                    }}
                  >
                    <AvatarWithLoader src={user.avatar} alt={user.name} />
                    <Typography variant="body4" color={"#4F4F4F"}>
                      {user.name || "Unknown User"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No participants yet
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="center" mt={4} gap={1}>
          <ArgonButton
            className="review-panel-action-button"
            onClick={handleClose}
          >
            Cancel
          </ArgonButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function MetaItem({ label, name, value, img }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="left" gap={1}>
      <Typography variant="h6" fontWeight={700} color="#000000">
        {label}
      </Typography>
      <Box display="flex" flexDirection="row" alignItems="center">
        <AvatarWithLoader src={img} alt={name} />
        <Box pb={1}>
          <Typography variant="h6" fontWeight={700} color="#7B809A">
            {name || value}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
export default ActivityReviewPanelModal;
